import React from 'react';
import './CleanPlayerCard.css';

interface CleanPlayerCardProps {
  /** Player's avatar/circle element */
  children: React.ReactNode;
  
  /** Player info */
  playerName: string;
  playerId: string;
  
  /** Scoring */
  currentGameScore?: number;  // Points in current game
  roomScore?: number;          // Total room leaderboard score
  rank?: number;               // Player's rank (1, 2, 3, etc.)
  
  /** Team & Role */
  team?: 'bidding' | 'opposing';
  isBidder?: boolean;
  isPartner?: boolean;
  isPartnerRevealed?: boolean;
  
  /** Turn indication */
  isCurrentTurn?: boolean;
}

export const CleanPlayerCard: React.FC<CleanPlayerCardProps> = ({
  children,
  playerName,
  currentGameScore,
  roomScore,
  rank,
  team,
  isBidder,
  isPartner,
  isPartnerRevealed,
  isCurrentTurn
}) => {
  // Determine game score color based on team/role
  const getGameScoreColor = () => {
    if (isBidder) return '#fbbf24'; // Yellow for bidder (primary indicator)
    if (isPartner && isPartnerRevealed) return '#22c55e'; // Green for revealed partner
    if (team === 'bidding') return '#22c55e'; // Green for bidding team members
    if (team === 'opposing') return '#ef4444'; // Red for opposing team
    return '#9ca3af'; // Gray fallback
  };

  return (
    <div className={`clean-player-card ${isCurrentTurn ? 'current-turn-glow' : ''}`}>
      {/* Player Name (above circle) */}
      <div className="player-name-label">{playerName}</div>
      
      {/* Player Avatar Circle (center) */}
      <div className="player-avatar-container">
        {children}
      </div>
      
      {/* Stats positioned around the circle */}
      
      {/* Top-Left: Rank Badge */}
      {rank && (
        <div className="stat-badge rank-badge">
          <span className="badge-icon">🔥</span>
          <span className="badge-value">{rank}</span>
        </div>
      )}
      
      {/* Top-Right: Current Game Score (colored by team) */}
      {typeof currentGameScore === 'number' && (
        <div 
          className="stat-badge game-score-badge"
          style={{ 
            background: getGameScoreColor(),
            color: '#000',
            fontWeight: 'bold'
          }}
        >
          {isBidder ? (
            <>
              <span className="badge-text">B</span>
              <span className="badge-value">{currentGameScore >= 0 ? '+' : ''}{currentGameScore}</span>
            </>
          ) : (
            <span className="badge-value">{currentGameScore >= 0 ? '+' : ''}{currentGameScore}</span>
          )}
        </div>
      )}
      
      {/* Bottom: Room Leaderboard Score */}
      {typeof roomScore === 'number' && (
        <div className="stat-badge room-score-badge">
          <span className="badge-icon">🏆</span>
          <span className="badge-value">{roomScore}</span>
        </div>
      )}
      
      {/* Partner indicator (small, non-intrusive) */}
      {isPartner && isPartnerRevealed && (
        <div className="partner-marker">🤝</div>
      )}
    </div>
  );
};

export default CleanPlayerCard;
